<template>
    <div class="rtb-campaign-statistics">
        <div class="header">
            <div>
                <h1>{{ heading }}</h1>
                <p v-if="campaign?.screen_package?.name" class="subtitle">Пакет: {{ campaign.screen_package.name }}</p>
            </div>
            <div class="controls">
                <input v-model="from" type="date" :min="minDate" :max="maxDate" />
                <input v-model="to" type="date" :min="minDate" :max="maxDate" />
                <button type="button" class="btn btn--primary" :disabled="loading" @click="applyRange">Применить</button>
                <button type="button" class="btn btn--secondary" :disabled="loading || exporting" @click="downloadReport">
                    {{ exporting ? 'Выгрузка…' : 'Скачать отчёт' }}
                </button>
            </div>
        </div>

        <div v-if="loading" class="loading">Загрузка...</div>
        <div v-if="error" class="error">{{ error }}</div>

        <div v-if="!loading && !error">
            <div v-if="audience.enabled" class="campaign-show__tabs rtb-stats-tabs">
                <button type="button" class="tab" :class="{ active: activeTab === 'delivery' }" @click="activeTab = 'delivery'">
                    Показы
                </button>
                <button type="button" class="tab" :class="{ active: activeTab === 'audience' }" @click="activeTab = 'audience'">
                    Аудитория
                </button>
            </div>

            <div v-show="activeTab === 'delivery'">
                <div class="metrics">
                    <div class="metric">
                        <div class="label">Показы (всего)</div>
                        <div class="value">{{ counters.impr_total_fact ?? money.impressions_total ?? 0 }}</div>
                    </div>
                    <div class="metric">
                        <div class="label">Показы (сегодня)</div>
                        <div class="value">{{ counters.impr_daily_fact ?? money.impressions_today ?? 0 }}</div>
                    </div>
                    <div v-for="col in moneyColumns" :key="col.key" class="metric">
                        <div class="label">{{ col.label }}</div>
                        <div class="value">{{ formatMoney(col.spent) }}</div>
                        <div class="label">сегодня {{ formatMoney(col.spent_today) }}</div>
                        <div v-if="col.remaining != null" class="label">остаток {{ formatMoney(col.remaining) }}</div>
                    </div>
                </div>

                <div v-if="byDay.length" class="chart-card">
                    <h3>Расходы и показы по дням</h3>
                    <div class="chart-canvas"><canvas ref="byDayChart"></canvas></div>
                </div>

                <div v-if="byHour.length" class="chart-card">
                    <h3>Расходы и показы по часам</h3>
                    <div class="chart-canvas"><canvas ref="byHourChart"></canvas></div>
                </div>

                <div v-if="byCreative.length" class="chart-card">
                    <h3>Расходы по креативам (все)</h3>
                    <div class="chart-canvas chart-canvas--wide"><canvas ref="byCreativeChart"></canvas></div>
                    <div class="table-wrap">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>Креатив</th>
                                    <th>Показы</th>
                                    <th v-for="col in moneyColumns" :key="col.key">{{ col.label }}</th>
                                    <th>Средняя стоимость</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in byCreative" :key="row.creative_id ?? row.creative_name">
                                    <td>
                                        <div>{{ row.creative_name }}</div>
                                        <div v-if="row.by_locale?.length" class="locale-breakdown">
                                            <span
                                                v-for="loc in row.by_locale"
                                                :key="loc.locale || 'unknown'"
                                                class="locale-breakdown__item"
                                            >
                                                {{ (loc.locale || '?').toUpperCase() }}: {{ loc.impressions }}
                                            </span>
                                        </div>
                                    </td>
                                    <td>{{ row.impressions }}</td>
                                    <td v-for="col in moneyColumns" :key="col.key">{{ formatMoney(spentByKey(row, col.key)) }}</td>
                                    <td>{{ formatMoney(row.avg_cost) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="byScreen.length" class="chart-card">
                    <h3>Расходы по экранам (все)</h3>
                    <div class="chart-canvas chart-canvas--wide"><canvas ref="byScreenChart"></canvas></div>
                    <div class="table-wrap">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>GID</th>
                                    <th>Адрес</th>
                                    <th>Показы</th>
                                    <th v-for="col in moneyColumns" :key="col.key">{{ col.label }}</th>
                                    <th>Средняя стоимость</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in byScreen" :key="row.gid">
                                    <td>{{ row.gid }}</td>
                                    <td>{{ row.address || '-' }}</td>
                                    <td>{{ row.impressions }}</td>
                                    <td v-for="col in moneyColumns" :key="col.key">{{ formatMoney(spentByKey(row, col.key)) }}</td>
                                    <td>{{ formatMoney(row.avg_cost) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="showBindings && byBinding.length" class="chart-card">
                    <h3>По связкам (креатив + экран)</h3>
                    <div class="table-wrap">
                        <table class="table">
                            <thead>
                                <tr>
                                    <th>Креатив</th>
                                    <th>GID</th>
                                    <th>Адрес</th>
                                    <th>Показы</th>
                                    <th v-for="col in moneyColumns" :key="col.key">{{ col.label }}</th>
                                    <th>Средняя стоимость</th>
                                    <th>Последний показ (KZ)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in byBinding" :key="row.ccf_id ?? `${row.creative_id}-${row.gid}`">
                                    <td>{{ row.creative_name }}</td>
                                    <td>{{ row.gid }}</td>
                                    <td>{{ row.frame_address || '-' }}</td>
                                    <td>{{ row.impressions }}</td>
                                    <td v-for="col in moneyColumns" :key="col.key">{{ formatMoney(spentByKey(row, col.key)) }}</td>
                                    <td>{{ formatMoney(row.avg_cost) }}</td>
                                    <td class="last-show-at-kz">{{ formatLastShowAtKz(row.last_show_at_kz) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <AudienceReportCard
                v-if="audience.enabled && activeTab === 'audience'"
                :audience="audience"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import axios from 'axios';
import Chart from 'chart.js/auto';
import AudienceReportCard from './AudienceReportCard.vue';
import { RTB_CAMPAIGN_EXPORT_PATH, RTB_CAMPAIGN_TYPE } from '../../../constants/rtbCampaigns';
import { saveBlobResponse, withParsedBlobError } from '../../../utils/download';
import { extractErrorMessage, notifyHttpError } from '../../../utils/httpErrors';
import { getRtbDateString } from '../../../utils/timezones';

const ChartCtor = Chart?.default ?? Chart;

const props = defineProps({
    campaign: { type: Object, required: true },
    userData: { type: Object, default: () => ({}) },
    type: { type: String, default: RTB_CAMPAIGN_TYPE.MANUAL },
});

const from = ref('');
const to = ref('');
const loading = ref(false);
const exporting = ref(false);
const error = ref(null);
const counters = ref({});
const byDay = ref([]);
const byHour = ref([]);
const byCreative = ref([]);
const byScreen = ref([]);
const byBinding = ref([]);
const money = ref({ columns: [] });
const audience = ref({ enabled: false });
const activeTab = ref('delivery');
const byDayChart = ref(null);
const byHourChart = ref(null);
const byCreativeChart = ref(null);
const byScreenChart = ref(null);
const charts = { byDay: null, byHour: null, byCreative: null, byScreen: null };

const heading = computed(() => (
    props.type === RTB_CAMPAIGN_TYPE.PACKAGE ? 'Статистика кампании по пакету' : 'Статистика'
));
const showBindings = computed(() => props.type === RTB_CAMPAIGN_TYPE.MANUAL);
const minDate = computed(() => props.campaign?.start_date ?? '');
const maxDate = computed(() => props.campaign?.end_date ?? '');
const moneyColumns = computed(() => money.value?.columns || []);
const primarySpentLabel = computed(() => {
    const key = money.value?.primary_key;
    const col = moneyColumns.value.find(item => item.key === key);
    return col?.label || 'Расход';
});

const formatMoney = (val) => new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 0,
}).format(val ?? 0);

const formatLastShowAtKz = (value) => {
    if (!value) return '—';
    const normalized = String(value).replace('T', ' ').slice(0, 19);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}:\d{2})$/);
    if (!match) return normalized;
    const [, year, month, day, time] = match;
    return `${time}\n${day}.${month}.${year}`;
};

const spentByKey = (row, key) => {
    if (row?.spent_by_key && row.spent_by_key[key] != null) return row.spent_by_key[key];
    return key === money.value?.primary_key ? (row?.spent ?? 0) : 0;
};

const destroyCharts = () => {
    Object.values(charts).forEach((chart) => {
        if (chart) chart.destroy();
    });
    charts.byDay = null;
    charts.byHour = null;
    charts.byCreative = null;
    charts.byScreen = null;
};

const getCanvasContext = (el) => {
    if (!el || typeof el.getContext !== 'function') throw new Error('Canvas is not available');
    const context = el.getContext('2d');
    if (!context) throw new Error('Unable to get 2d context');
    return context;
};

const createChart = (el, config) => new ChartCtor(getCanvasContext(el), config);

const renderByDayChart = () => {
    if (!byDay.value.length || !byDayChart.value) return;
    charts.byDay = createChart(byDayChart.value, {
        type: 'bar',
        data: {
            labels: byDay.value.map(row => row.date),
            datasets: [
                { label: 'Показы', data: byDay.value.map(row => row.impressions), yAxisID: 'yImpressions', backgroundColor: '#93c5fd' },
                { label: primarySpentLabel.value, data: byDay.value.map(row => row.spent), yAxisID: 'ySpent', borderColor: '#2563eb', backgroundColor: '#2563eb', type: 'line', tension: 0.2 },
            ],
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                yImpressions: { type: 'linear', position: 'left', beginAtZero: true, title: { display: true, text: 'Показы' } },
                ySpent: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false }, title: { display: true, text: primarySpentLabel.value } },
            },
        },
    });
};

const renderByHourChart = () => {
    if (!byHour.value.length || !byHourChart.value) return;
    charts.byHour = createChart(byHourChart.value, {
        type: 'bar',
        data: {
            labels: byHour.value.map(row => `${String(row.hour).padStart(2, '0')}:00`),
            datasets: [
                { label: primarySpentLabel.value, data: byHour.value.map(row => row.spent), backgroundColor: '#10b981' },
                { label: 'Показы', data: byHour.value.map(row => row.impressions), borderColor: '#1d4ed8', backgroundColor: '#1d4ed8', type: 'line', tension: 0.2 },
            ],
        },
        options: { responsive: true, maintainAspectRatio: false },
    });
};

const renderByCreativeChart = () => {
    if (!byCreative.value.length || !byCreativeChart.value) return;
    charts.byCreative = createChart(byCreativeChart.value, {
        type: 'bar',
        data: {
            labels: byCreative.value.map(row => row.creative_name),
            datasets: [{ label: primarySpentLabel.value, data: byCreative.value.map(row => row.spent), backgroundColor: '#6366f1' }],
        },
        options: { responsive: true, maintainAspectRatio: false, indexAxis: 'y' },
    });
};

const renderByScreenChart = () => {
    if (!byScreen.value.length || !byScreenChart.value) return;
    charts.byScreen = createChart(byScreenChart.value, {
        type: 'bar',
        data: {
            labels: byScreen.value.map(row => row.gid),
            datasets: [{ label: primarySpentLabel.value, data: byScreen.value.map(row => row.spent), backgroundColor: '#f59e0b' }],
        },
        options: { responsive: true, maintainAspectRatio: false, indexAxis: 'y' },
    });
};

const renderCharts = () => {
    destroyCharts();
    try {
        renderByDayChart();
        renderByHourChart();
        renderByCreativeChart();
        renderByScreenChart();
    } catch {
        if (!error.value) error.value = 'Ошибка отображения графиков. Проверьте данные статистики.';
    }
};

const normalizeStatsPayload = (payload) => {
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
        throw new Error('Невалидный ответ API статистики');
    }
    const days = Array.isArray(payload.by_day) ? payload.by_day : (Array.isArray(payload.timeseries) ? payload.timeseries : null);
    const hours = Array.isArray(payload.by_hour) ? payload.by_hour : null;
    const creatives = Array.isArray(payload.by_creative) ? payload.by_creative : null;
    const screens = Array.isArray(payload.by_screen) ? payload.by_screen : null;
    if (days === null || hours === null || creatives === null || screens === null) {
        throw new Error('API вернул некорректную структуру статистики');
    }
    return {
        counters: payload.counters && typeof payload.counters === 'object' && !Array.isArray(payload.counters) ? payload.counters : {},
        money: payload.money && typeof payload.money === 'object' && !Array.isArray(payload.money) ? payload.money : { columns: [] },
        byDay: days,
        byHour: hours,
        byCreative: creatives,
        byScreen: screens,
        byBinding: Array.isArray(payload.by_binding) ? payload.by_binding : [],
        audience: payload.audience || { enabled: false },
    };
};

const fetchStats = async () => {
    loading.value = true;
    error.value = null;
    try {
        const resp = await axios.get(`/api/rtb/campaigns/${props.campaign.id}/stats`, {
            params: { from: from.value, to: to.value },
        });
        const normalized = normalizeStatsPayload(resp?.data);
        counters.value = normalized.counters;
        money.value = normalized.money;
        byDay.value = normalized.byDay;
        byHour.value = normalized.byHour;
        byCreative.value = normalized.byCreative;
        byScreen.value = normalized.byScreen;
        byBinding.value = normalized.byBinding;
        audience.value = normalized.audience;
    } catch (e) {
        error.value = extractErrorMessage(e, 'Не удалось загрузить статистику');
    } finally {
        loading.value = false;
    }

    if (!error.value) {
        await nextTick();
        renderCharts();
    }
};

const applyRange = () => {
    if (!from.value || !to.value) {
        error.value = 'Укажите обе даты';
        return;
    }
    if (from.value > to.value) {
        error.value = 'Дата начала не может быть позже даты окончания';
        return;
    }
    fetchStats();
};

const downloadReport = async () => {
    if (exporting.value) return;
    exporting.value = true;
    try {
        const response = await axios.get(RTB_CAMPAIGN_EXPORT_PATH[props.type](props.campaign.id), {
            params: { from: from.value, to: to.value },
            responseType: 'blob',
        });
        saveBlobResponse(response, `campaign-${props.campaign.id}-report.xlsx`);
    } catch (e) {
        notifyHttpError(await withParsedBlobError(e), 'Не удалось скачать отчёт');
    } finally {
        exporting.value = false;
    }
};

watch(() => audience.value.enabled, (enabled) => {
    if (!enabled) activeTab.value = 'delivery';
});
watch(activeTab, (tab) => {
    if (tab !== 'delivery') return;
    nextTick(() => {
        Object.values(charts).forEach((chart) => {
            if (chart && typeof chart.resize === 'function') chart.resize();
        });
    });
});

onMounted(() => {
    const end = props.campaign?.end_date?.slice(0, 10) || getRtbDateString();
    const start = props.campaign?.start_date?.slice(0, 10) || end;
    const endDate = new Date(`${end}T00:00:00`);
    const startDate = new Date(`${start}T00:00:00`);
    const fromDate = new Date(endDate);
    fromDate.setDate(endDate.getDate() - 29);
    if (fromDate < startDate) fromDate.setTime(startDate.getTime());
    from.value = getRtbDateString(fromDate);
    to.value = end;
    fetchStats();
});

onBeforeUnmount(destroyCharts);
</script>

<style lang="scss" scoped>
.rtb-campaign-statistics {
    padding: 24px;
    max-width: 1200px;
    margin: 0 auto;
}

.header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.subtitle {
    margin: 4px 0 0;
    color: #6b7280;
}

.controls {
    display: flex;
    align-items: center;
    gap: 8px;
}

.controls input {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
}

.metrics {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

.metric {
    background: #fff;
    border: 1px solid #e5e7eb;
    padding: 12px;
    border-radius: 8px;
    min-width: 160px;
}

.label {
    color: #6b7280;
    font-size: 12px;
}

.value {
    font-weight: 600;
    font-size: 16px;
    margin-top: 6px;
}

.chart-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 16px;
}

.chart-card h3 {
    margin: 0 0 12px 0;
}

.chart-canvas {
    height: 320px;
}

.chart-canvas--wide {
    height: 420px;
}

.table-wrap {
    margin-top: 12px;
    max-height: 360px;
    overflow: auto;
}

.table {
    width: 100%;
    border-collapse: collapse;
}

.table th,
.table td {
    border-bottom: 1px solid #e5e7eb;
    padding: 8px;
    text-align: left;
    white-space: nowrap;
}

.last-show-at-kz {
    white-space: pre-line;
    line-height: 1.35;
}

.locale-breakdown {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
    white-space: normal;
}

.locale-breakdown__item {
    font-size: 11px;
    color: #64748b;
    background: #f1f5f9;
    border-radius: 999px;
    padding: 2px 8px;
}

.loading {
    padding: 20px;
    text-align: center;
}

.error {
    color: #ef4444;
    padding: 12px;
}
</style>
